import { useEffect, useState } from 'react';
import { useAsyncStatus } from '../../../../../hooks';
import * as API from '../../../../../redux/api';
import { nuc } from '../../../../../utils';
import type { License, LicenseSummary } from '../../../../../types';
import type { FailedFetchResponse } from '../../../../../helpers/errors';

type LicenseResponse = License & {
  Summary?: LicenseSummary;
};

export type FileMatchLicense = Pick<License, 'name'> & Partial<{
  description: LicenseSummary['summary'];
}>;

/**
 * Very simple API query for a license.
 */
function useLicense(licenseId: string) {
  const { status, onPending, onSuccess, onError } = useAsyncStatus();
  const [license, setLicense] = useState<FileMatchLicense | null>(null);

  useEffect(() => {
    async function getLicense() {
      try {
        const url = `/api/licenses/${nuc(licenseId)}`;
        onPending();
        const { data } = await API.get<LicenseResponse>(url);

        if (data) {
          const { name, Summary } = data;
          if (Summary) {
            setLicense({ name, description: Summary.summary });
          } else {
            setLicense({ name });
          }

          onSuccess();
        } else {
          onError(`License "${licenseId}" could not be fetched!`);
        }
      } catch (error) {
        onError(error as FailedFetchResponse);
      }
    }

    //  Guard against falsey `revisionId`, since it'd be a BadRequestError
    if (licenseId) {
      getLicense();
    }
  }, [licenseId, onPending, onSuccess, onError]);

  return {
    status,
    license,
  };
}

export default useLicense;
